import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Cell
} from "recharts";
import { Card } from "react-bootstrap";

// Enhanced category mapping function
const getCategory = (value, dataType) => {
  if (dataType === 'stress') {
    if (value <= 2) return "Low";
    if (value <= 4) return "Mild";
    if (value <= 6) return "Moderate";
    if (value <= 8) return "High";
    return "Severe";
  }
  
  if (dataType === 'wrmsd') {
    if (value === 0) return "No Pain";
    if (value <= 2) return "Mild";
    if (value <= 5) return "Moderate To Severe";
    if (value <= 8) return "Very Severe";
    return "Worst Pain Possible";
  }
  
  // Default for load and productivity
  if (value <= 5) return "Low";
  if (value <= 10) return "Regular";
  if (value <= 15) return "Moderate";
  return "High";
};

// Get category boundaries for reference lines
const getCategoryBoundaries = (dataType) => {
  if (dataType === 'stress') {
    return [
      { value: 2, label: "Low", color: "#e6e6ff" },
      { value: 4, label: "Mild", color: "#ccccff" },
      { value: 6, label: "Moderate", color: "#9999ff" },
      { value: 8, label: "High", color: "#6666cc" },
      { value: 10, label: "Severe", color: "#ff9966" }
    ];
  }
  
  if (dataType === 'wrmsd') {
    return [
      { value: 0, label: "No Pain", color: "#e6e6ff" },
      { value: 2, label: "Mild", color: "#ccccff" },
      { value: 5, label: "Moderate To Severe", color: "#9999ff" },
      { value: 8, label: "Very Severe", color: "#6666cc" },
      { value: 10, label: "Worst Pain Possible", color: "#ff9966" }
    ];
  }
  
  return [
    { value: 5, label: "Low", color: "#ccccff" },
    { value: 10, label: "Regular", color: "#9999ff" },
    { value: 15, label: "Moderate", color: "#6666cc" },
    { value: 20, label: "High", color: "#ff9966" }
  ];
};

// Color mapping for different categories
const getCategoryColor = (value, dataType) => {
  const category = getCategory(value, dataType);
  
  if (dataType === 'stress') {
    const colors = {
      Low: "#e6e6ff",        // Light purple
      Mild: "#ccccff",       // Light purple
      Moderate: "#9999ff",   // Medium purple
      High: "#6666cc",       // Dark purple
      Severe: "#ff9966"      // Orange
    };
    return colors[category];
  }
  
  if (dataType === 'wrmsd') {
    const colors = {
      "No Pain": "#e6e6ff",           // Light purple
      Mild: "#ccccff",                // Light purple
      "Moderate To Severe": "#9999ff", // Medium purple
      "Very Severe": "#6666cc",       // Dark purple
      "Worst Pain Possible": "#ff9966" // Orange
    };
    return colors[category];
  }
  
  // Default colors for load and productivity
  const colors = {
    Low: "#ccccff",
    Regular: "#9999ff",
    Moderate: "#6666cc",
    High: "#ff9966"
  };
  return colors[category];
};

const CardStats = ({ 
  data1 = [], 
  data2 = [], 
  show = false,
  data1Type = "load",
  data2Type = "productivity",
  title1 = "Workload",
  title2 = "Productivity"
}) => {
  const chartData = useMemo(() => {
    const departments = new Set([
      ...data1.map((d) => d.departmentName),
      ...data2.map((d) => d.departmentName),
    ]);

    return Array.from(departments).map((department) => {
      const item1 = data1.find((d) => d.departmentName === department);
      const item2 = data2.find((d) => d.departmentName === department);
      
      const value1 = item1?.value || 0;
      const value2 = item2?.value || 0;

      return {
        department,
        value1,
        value2,
        category1: item1?.category || getCategory(value1, data1Type),
        category2: item2?.category || getCategory(value2, data2Type),
        color1: getCategoryColor(value1, data1Type),
        color2: getCategoryColor(value2, data2Type)
      };
    });
  }, [data1, data2, data1Type, data2Type]);

  // Get dynamic Y-axis domain
  const getYAxisDomain = () => {
    if (data1Type === 'stress' || data1Type === 'wrmsd' || 
        data2Type === 'stress' || data2Type === 'wrmsd') {
      return [0, 10];
    }
    return [0, 20];
  };

  // Custom tooltip to show categories
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '12px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ fontWeight: 'bold', margin: '0 0 8px 0' }}>
            {`Department: ${label}`}
          </p>
          <p style={{ color: '#0066cc', margin: '4px 0' }}>
            {`${title1}: ${data.value1}`}
            <span style={{
              marginLeft: '8px',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '11px',
              color: 'white',
              backgroundColor: data.color1
            }}>
              {data.category1}
            </span>
          </p>
          <p style={{ color: '#009900', margin: '4px 0' }}>
            {`${title2}: ${data.value2}`}
            <span style={{
              marginLeft: '8px',
              padding: '2px 6px',
              borderRadius: '3px',
              fontSize: '11px',
              color: 'white',
              backgroundColor: data.color2
            }}>
              {data.category2}
            </span>
          </p>
        </div>
      );
    }
    return null;
  };

  // Get boundaries for reference lines
  const boundaries1 = getCategoryBoundaries(data1Type);

  return (
    <Card className="p-3 shadow-sm">
      <div style={{ marginBottom: '16px' }}>
        <h5 style={{ fontSize: '16px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>
          {title1} vs {title2} by Department
        </h5>
        <p style={{ fontSize: '12px', color: '#666', margin: 0 }}>
          Bars are colored by category ranges. Hover to see category details.
        </p>
      </div>

      {/* Category Legend */}
      <div style={{ marginBottom: '16px', display: 'flex', flexWrap: 'wrap', gap: '16px' }}>
        <div>
          <h6 style={{ fontSize: '12px', fontWeight: '500', color: '#555', marginBottom: '8px' }}>
            {title1} Categories:
          </h6>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {boundaries1.map((boundary, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div 
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: boundary.color
                  }}
                />
                <span style={{ fontSize: '11px', color: '#666' }}>{boundary.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div>
          <h6 style={{ fontSize: '12px', fontWeight: '500', color: '#555', marginBottom: '8px' }}>
            {title2} Categories:
          </h6>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {getCategoryBoundaries(data2Type).map((boundary, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <div 
                  style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '2px',
                    backgroundColor: getCategoryColor(boundary.value, data2Type),
                    opacity: 0.7
                  }}
                />
                <span style={{ fontSize: '11px', color: '#666' }}>{boundary.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ResponsiveContainer width="100%" height={450}>
        <BarChart
          data={chartData}
          barCategoryGap={20}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          
          {/* Reference lines for category boundaries */}
          {boundaries1.slice(0, -1).map((boundary, idx) => (
            <ReferenceLine 
              key={`ref1-${idx}`}
              y={boundary.value} 
              stroke={boundary.color} 
              strokeDasharray="2 2"
              strokeOpacity={0.3}
            />
          ))}

          <XAxis 
            dataKey="department"
            tick={{ fontSize: 12 }}
            interval={0}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis
            domain={getYAxisDomain()}
            label={{
              value: `${title1} & ${title2} Values`,
              angle: -90,
              position: "insideLeft",
            }}
            tick={{ fontSize: 12 }}
          />
          
          <Tooltip content={<CustomTooltip />} />
          
          {show && <Legend />}

          <Bar 
            dataKey="value1" 
            name={title1}
            fill="#8884d8"
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-1-${index}`} fill={entry.color1} />
            ))}
          </Bar>
          
          <Bar 
            dataKey="value2" 
            name={title2}
            fill="#82ca9d"
            fillOpacity={0.7}
          >
            {chartData.map((entry, index) => (
              <Cell key={`cell-2-${index}`} fill={entry.color2} opacity={0.7} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

    </Card>
  );
};

// THIS IS THE IMPORTANT PART - Make sure you have this default export
export default CardStats;
